import React, { useState } from 'react';
import { FaCheckCircle, FaTimesCircle, FaPlus, FaEdit } from "react-icons/fa";

const AllPackages = () => {
  // Static package data
  const [packages, setPackages] = useState([
    {
      _id: "1",
      name: "Starter Pack",
      description: "Perfect for light flow days and beginners. Comfortable and affordable.",
      popular: false,
      isActive: true,
      extraBenefits: "Standard pricing",
      plans: {
        "Base Product": {
          price: 299,
          pads: 20,
          rate: "₹14.95/pad"
        }
      }
    },
    {
      _id: "2",
      name: "Everyday Comfort",
      description: "Ideal for regular use with extra absorbency and skin-friendly material.",
      popular: true,
      isActive: true,
      extraBenefits: "Free delivery on this pack",
      plans: {
        "Base Product": {
          price: 549,
          pads: 40,
          rate: "₹13.73/pad"
        }
      }
    },
    {
      _id: "3",
      name: "Heavy Flow Pro",
      description: "Maximum protection for heavy flow days. Overnight safe with wings.",
      popular: false,
      isActive: true,
      extraBenefits: "10% extra pads included",
      plans: {
        "Base Product": {
          price: 799,
          pads: 60,
          rate: "₹13.32/pad"
        }
      }
    },
    {
      _id: "4",
      name: "Family Saver",
      description: "Best value pack for multiple users or long-term stocking.",
      popular: false,
      isActive: true,
      extraBenefits: "20% savings + free cotton pouch",
      plans: {
        "Base Product": {
          price: 1299,
          pads: 120,
          rate: "₹10.83/pad"
        }
      }
    },
    {
      _id: "5",
      name: "Trial Pack (Inactive)",
      description: "Old trial pack - no longer available.",
      popular: false,
      isActive: false,
      extraBenefits: "Discontinued",
      plans: {
        "Base Product": {
          price: 99,
          pads: 8,
          rate: "₹12.38/pad"
        }
      }
    }
  ]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null); // null = create, object = edit

  const formatINRCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const openCreateModal = () => {
    setEditingPackage(null);
    setIsModalOpen(true);
  };

  const openEditModal = (pkg) => {
    setEditingPackage(pkg);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingPackage(null);
  };

  const handleSave = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const newPackage = {
      _id: editingPackage?._id || Date.now().toString(),
      name: formData.get('name'),
      description: formData.get('description'),
      popular: formData.get('popular') === 'on',
      isActive: formData.get('isActive') === 'on',
      extraBenefits: formData.get('extraBenefits'),
      plans: {
        "Base Product": {
          price: Number(formData.get('price')),
          pads: Number(formData.get('pads')),
          rate: formData.get('rate'),
        }
      }
    };

    if (editingPackage) {
      setPackages(packages.map(p => p._id === editingPackage._id ? newPackage : p));
    } else {
      setPackages([...packages, newPackage]);
    }

    closeModal();
  };

  const PinkPackageCard = ({ pkg }) => {
    const basePlan = pkg.plans?.["Base Product"] || {};

    return (
      <div className='group relative bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-400 overflow-hidden transform hover:-translate-y-3 border border-pink-100 w-full max-w-lg mx-auto'>
        {/* Active / Inactive Indicator */}
        <div className='absolute top-4 right-4 z-10'>
          {pkg.isActive ? (
            <FaCheckCircle className='text-green-500 text-3xl drop-shadow-lg' />
          ) : (
            <FaTimesCircle className='text-red-500 text-3xl drop-shadow-lg' />
          )}
        </div>

        {/* Recommended Badge */}
        {pkg.popular && (
          <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-pink-500 text-white text-[10px] font-black px-5 py-1.5 rounded-full shadow-lg z-20 uppercase tracking-widest">
            Recommended
          </div>
        )}

        {/* Image Placeholder */}
        <div className='w-full h-72 overflow-hidden bg-gradient-to-b from-pink-50 to-white flex items-center justify-center'>
          <div className='text-pink-300 text-6xl font-black opacity-20'>
            {pkg.name.charAt(0).toUpperCase()}
          </div>
        </div>

        {/* Card Body */}
        <div className='p-8 bg-gradient-to-b from-white to-pink-50'>
          <h3 className='font-bold text-2xl text-gray-800 line-clamp-2'>
            {pkg.name}
          </h3>
          <p className='text-pink-700 text-base mt-3 font-medium line-clamp-3'>
            {pkg.description}
          </p>

          {/* Price */}
          <div className='mt-6 flex items-end gap-4'>
            <p className='text-4xl font-extrabold text-pink-600'>
              {formatINRCurrency(basePlan.price || 0)}
            </p>
            <span className='text-xl text-gray-500 font-medium pb-1'>/ Pack</span>
          </div>

          {/* Details */}
          <div className='mt-5 space-y-3'>
            <p className='text-base font-bold text-pink-800'>
              {basePlan.pads || 0} Pads Total
            </p>
            <p className='text-sm text-pink-600 font-semibold'>
              Effective Rate: {basePlan.rate || "N/A"}
            </p>
            {pkg.extraBenefits && pkg.extraBenefits !== "Standard pricing" && (
              <p className='text-sm text-pink-800 italic'>
                ★ {pkg.extraBenefits}
              </p>
            )}
          </div>

          {/* Edit Button */}
          <button
            onClick={() => openEditModal(pkg)}
            className='mt-8 w-full py-4 bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 text-white font-bold text-lg rounded-xl shadow-md transition flex items-center justify-center gap-3'
          >
            <FaEdit />
            Edit Package
          </button>
        </div>

        {/* Hover Glow */}
        <div className='absolute inset-0 rounded-2xl ring-4 ring-pink-300 opacity-0 group-hover:opacity-30 transition-opacity pointer-events-none'></div>
      </div>
    );
  };

  return (
    <div className='max-w-7xl mx-auto mt-2 px-4 py-10'>
      {/* Header */}
      <div className='flex justify-between items-center mb-10'>
        <h1 className='text-2xl md:text-4xl font-extrabold text-pink-800 tracking-wide'>
          All Subscription Packages
        </h1>

        {/* Create Package Button */}
        <button
          onClick={openCreateModal}
          className='flex items-center gap-3 px-6 py-3 bg-pink-600 hover:bg-pink-700 text-white font-bold rounded-xl shadow-md transition'
        >
          <FaPlus />
          Create Package
        </button>
      </div>

      {/* Grid: Exactly 3 per row on large screens */}
      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12'>
        {packages.map((pkg) => (
          <PinkPackageCard key={pkg._id} pkg={pkg} />
        ))}
      </div>

      {/* Empty State */}
      {packages.length === 0 && (
        <div className="text-center py-20">
          <p className="text-gray-500 text-lg">No packages available. Create one to get started!</p>
        </div>
      )}

      {/* Modal */}
      {isModalOpen && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
          <div className='bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-screen overflow-y-auto'>
            <div className='p-8'>
              <h2 className='text-3xl font-bold text-pink-800 mb-6'>
                {editingPackage ? 'Edit Package' : 'Create New Package'}
              </h2>

              <form onSubmit={handleSave}>
                <div className='space-y-5'>
                  <div>
                    <label className='block text-sm font-bold text-gray-700 mb-2'>Package Name</label>
                    <input
                      type="text"
                      name="name"
                      defaultValue={editingPackage?.name}
                      required
                      className='w-full px-4 py-3 border border-pink-200 rounded-xl focus:outline-none focus:border-pink-500'
                    />
                  </div>

                  <div>
                    <label className='block text-sm font-bold text-gray-700 mb-2'>Description</label>
                    <textarea
                      name="description"
                      defaultValue={editingPackage?.description}
                      rows="3"
                      required
                      className='w-full px-4 py-3 border border-pink-200 rounded-xl focus:outline-none focus:border-pink-500'
                    />
                  </div>

                  <div className='grid grid-cols-2 gap-4'>
                    <div>
                      <label className='block text-sm font-bold text-gray-700 mb-2'>Price (₹)</label>
                      <input
                        type="number"
                        name="price"
                        defaultValue={editingPackage?.plans?.["Base Product"]?.price}
                        required
                        className='w-full px-4 py-3 border border-pink-200 rounded-xl focus:outline-none focus:border-pink-500'
                      />
                    </div>
                    <div>
                      <label className='block text-sm font-bold text-gray-700 mb-2'>Total Pads</label>
                      <input
                        type="number"
                        name="pads"
                        defaultValue={editingPackage?.plans?.["Base Product"]?.pads}
                        required
                        className='w-full px-4 py-3 border border-pink-200 rounded-xl focus:outline-none focus:border-pink-500'
                      />
                    </div>
                  </div>

                  <div>
                    <label className='block text-sm font-bold text-gray-700 mb-2'>Effective Rate (e.g. ₹12.50/pad)</label>
                    <input
                      type="text"
                      name="rate"
                      defaultValue={editingPackage?.plans?.["Base Product"]?.rate}
                      required
                      className='w-full px-4 py-3 border border-pink-200 rounded-xl focus:outline-none focus:border-pink-500'
                    />
                  </div>

                  <div>
                    <label className='block text-sm font-bold text-gray-700 mb-2'>Extra Benefits</label>
                    <input
                      type="text"
                      name="extraBenefits"
                      defaultValue={editingPackage?.extraBenefits}
                      placeholder="e.g. Free delivery, 10% extra pads"
                      className='w-full px-4 py-3 border border-pink-200 rounded-xl focus:outline-none focus:border-pink-500'
                    />
                  </div>

<div className="flex gap-10">
  {/* Popular Toggle */}
  <label className="flex items-center gap-4 cursor-pointer">
    <span className="font-medium text-gray-700">
      Mark as Popular (Recommended)
    </span>

    <div className="relative">
      <input
        type="checkbox"
        name="popular"
        defaultChecked={editingPackage?.popular}
        className="sr-only peer"
      />
      <div className="w-11 h-6 bg-gray-300 rounded-full peer peer-checked:bg-pink-600 transition"></div>
      <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition peer-checked:translate-x-5"></div>
    </div>
  </label>

  {/* Active Toggle */}
  <label className="flex items-center gap-4 cursor-pointer">
    <span className="font-medium text-gray-700">
      Active Package
    </span>

    <div className="relative">
      <input
        type="checkbox"
        name="isActive"
        defaultChecked={editingPackage?.isActive !== false}
        className="sr-only peer"
      />
      <div className="w-11 h-6 bg-gray-300 rounded-full peer peer-checked:bg-green-600 transition"></div>
      <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition peer-checked:translate-x-5"></div>
    </div>
  </label>
</div>

                </div>

                <div className='flex justify-end gap-4 mt-10'>
                  <button
                    type="button"
                    onClick={closeModal}
                    className='px-6 py-3 bg-gray-300 text-gray-700 font-bold rounded-xl hover:bg-gray-400 transition'
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className='px-8 py-3 bg-pink-600 hover:bg-pink-700 text-white font-bold rounded-xl transition shadow-md'
                  >
                    {editingPackage ? 'Update Package' : 'Create Package'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllPackages;